import React, { useState } from "react";
import { Pencil, Tags, Trash2 } from "lucide-react";
import {
  companyFormConfirm,
  companyFormToast,
  companyFormToastError,
} from "./companyFormSwal";
import CompanyFormsAddCategoryField from "./CompanyFormsAddCategoryField";
import CompanyFormsEditCategoryModal from "./CompanyFormsEditCategoryModal";
import CompanyFormsInfoBanner from "./CompanyFormsInfoBanner";

export default function CompanyFormsCategoriesPanel({
  categories,
  canManage,
  onCreate,
  onUpdate,
  onDelete,
  fullPage = false,
}) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [busyId, setBusyId] = useState(null);

  if (!canManage) return null;

  const startEdit = (cat) => {
    if (!cat?.id) return;
    setEditingCategory({ id: cat.id, label: cat.label });
  };

  const remove = async (cat) => {
    const result = await companyFormConfirm({
      title: "Remove category?",
      text: `"${cat.label}" will be hidden from upload and filters. It must not be used by any document.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Remove",
    });
    if (!result.isConfirmed) return;

    setBusyId(cat.id);
    try {
      await onDelete(cat.id);
      companyFormToast({ title: "Removed", timer: 1800 });
    } catch (error) {
      companyFormToastError(
        "Could not remove",
        error.response?.data?.message || error.message,
      );
    } finally {
      setBusyId(null);
    }
  };

  const renderActions = (cat) => (
    <div className="flex shrink-0 items-center justify-end gap-1">
      <button
        type="button"
        title="Rename category"
        aria-label={`Rename ${cat.label}`}
        disabled={busyId === cat.id}
        onClick={() => startEdit(cat)}
        className="company-forms-icon-btn company-forms-icon-btn--ghost"
      >
        <Pencil size={13} strokeWidth={1.75} aria-hidden="true" />
      </button>
      <button
        type="button"
        title="Remove category"
        aria-label={`Remove ${cat.label}`}
        disabled={busyId === cat.id}
        onClick={() => remove(cat)}
        className="company-forms-icon-btn company-forms-icon-btn--danger"
      >
        <Trash2 size={13} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  );

  const editModal = (
    <CompanyFormsEditCategoryModal
      open={Boolean(editingCategory)}
      category={editingCategory}
      onClose={() => setEditingCategory(null)}
      onUpdate={onUpdate}
    />
  );

  if (fullPage) {
    return (
      <section className="company-forms-categories-panel company-forms-categories-panel--page">
        {editModal}
        <CompanyFormsInfoBanner bannerKey="categories-info" icon={Tags}>
          <span className="font-medium text-[#14213D]">
            <span className="font-semibold text-[#007BFF]">{categories.length}</span>{" "}
            {categories.length === 1 ? "category" : "categories"} in use for uploads and filters.
          </span>
          <span className="text-[#64748B]">
            {" "}
            Remove a category only when no documents reference it.
          </span>
        </CompanyFormsInfoBanner>
        <div className="overflow-x-auto rounded-xl border border-[#E5ECF5] bg-white shadow-[0_1px_2px_rgba(20,33,61,0.05)]">
          <table className="company-forms-data-table company-forms-categories-table">
            <thead>
              <tr>
                <th>Category name</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={2} className="py-10 text-center text-sm text-[#64748B]">
                    No categories yet. Add one to use when uploading documents.
                  </td>
                </tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id ?? cat.value}>
                    <td>
                      <span className="text-sm font-medium text-[#14213D]">{cat.label}</span>
                    </td>
                    <td className="text-right">
                      {cat.id ? renderActions(cat) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  return (
    <section className="company-forms-categories-panel mb-4">
      {editModal}
      <div className="rounded-xl border border-[#E5ECF5] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(20,33,61,0.05)] sm:px-5">
        <div className="mb-3">
          <p className="text-sm font-semibold text-[#14213D]">Categories</p>
          <p className="mt-1 text-xs leading-snug text-[#64748B]">
            Categories appear in the upload dialog and document filters. Remove a category only when
            no documents use it.
          </p>
        </div>
        <ul className="divide-y divide-slate-100">
          {categories.map((cat) => (
            <li
              key={cat.id ?? cat.value}
              className="flex flex-wrap items-center justify-between gap-2 py-2.5"
            >
              <span className="text-sm font-medium text-[#14213D]">{cat.label}</span>
              {cat.id ? renderActions(cat) : null}
            </li>
          ))}
        </ul>
        <CompanyFormsAddCategoryField onCreate={onCreate} />
      </div>
    </section>
  );
}
